import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { Button } from '../../../../../components/ui/button';
import { toast } from 'react-hot-toast';
import type { Materials } from '../../../types/materials';
import { PipeSize } from '../../../types/setup';
import { DocumentTag } from '@/features/project/types';
import { pipeSizes } from '../../../mocks/material';

interface AddMaterialDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (material: Materials) => void;
  selectedPipeSize: number;
}





export const AddMaterialDialog: React.FC<AddMaterialDialogProps> = ({
  isOpen,
  onClose,
  onAdd,
  selectedPipeSize
}) => {
  const [documentTags, setDocumentTags] = useState<DocumentTag[]>([]);
  const [pipeSize, setPipeSize] = useState<PipeSize[]>(pipeSizes);
  const [newMaterial, setNewMaterial] = useState<Partial<Materials>>({
    category: 'Pipe',
    pipeId: '',
    status: 'Available'
  });
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [materialDocuments, setMaterialDocuments] = useState<File[]>([]);

  const handleAddMaterial = () => {
    if (!newMaterial.category) {
      toast.error('Please select a material category');
      return;
    }

    if (newMaterial.category && newMaterial.pipeId) {
      const material: Materials = {
        id: Math.random().toString(36).substr(2, 9),
        category: newMaterial.category,
        pipeId: newMaterial.pipeId,
        status: newMaterial.status || 'Available'
      };

      // Add category-specific fields
      if (newMaterial.category === 'Pipe') {
        material.pipeNRI = newMaterial.pipeNRI;
        material.pipeNo = newMaterial.pipeNo;
        material.heatNumber = newMaterial.heatNumber;
        material.length = newMaterial.length;
        material.outerDiameter = newMaterial.outerDiameter;
        material.wallThickness = newMaterial.wallThickness;
      } else if (newMaterial.category === 'Forge') {
        material.forgeNRI = newMaterial.forgeNRI;
        material.forgeOrderNumber = newMaterial.forgeOrderNumber;
        material.forgeSerialNumber = newMaterial.forgeSerialNumber;
        material.forgeHeatNumber = newMaterial.forgeHeatNumber;
      } else if (newMaterial.category === 'Gas') {
        material.gasSerialNumber = newMaterial.gasSerialNumber;
      } else if (newMaterial.category === 'Wire Lot') {
        material.lotNumber = newMaterial.lotNumber;
      }

      onAdd(material);
      setNewMaterial({
        category: 'Pipe',
        pipeId: '',
        status: 'Available'
      });
      onClose();
    }
  };

  const handleAddTag = () => {
    if (newTag.trim()) {
      const newDocTag = {
        id: Math.random().toString(36).substr(2, 9),
        name: newTag.trim()
      };
      setDocumentTags([...documentTags, newDocTag]);
      setNewTag('');
      setIsAddingTag(false);
    }
  };

  const handleMaterialFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setMaterialDocuments([...materialDocuments, ...Array.from(e.target.files)]);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Add Material</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Material Category</Label>
              <Select
                value={newMaterial.category}
                onValueChange={(value: 'Pipe' | 'Forge' | 'Gas' | 'Wire Lot') => 
                  setNewMaterial(prev => ({ ...prev, category: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pipe">Pipe</SelectItem>
                  <SelectItem value="Forge">Forge</SelectItem>
                  <SelectItem value="Gas">Gas</SelectItem>
                  <SelectItem value="Wire Lot">Wire Lot</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Dynamic fields based on category */}
            {newMaterial.category === 'Pipe' && (
              <>
                <div className="space-y-2">
                  <Label>Pipe NRI</Label>
                  <Input
                    value={newMaterial.pipeNRI}
                    onChange={(e) => setNewMaterial(prev => ({ ...prev, pipeNRI: e.target.value }))}
                    placeholder="Enter Pipe NRI"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Pipe No</Label>
                  <Input
                    value={newMaterial.pipeNo}
                    onChange={(e) => setNewMaterial(prev => ({ ...prev, pipeNo: e.target.value }))}
                    placeholder="Enter Pipe No"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Heat Number</Label>
                  <Input
                    value={newMaterial.heatNumber}
                    onChange={(e) => setNewMaterial(prev => ({ ...prev, heatNumber: e.target.value }))}
                    placeholder="Enter Heat Number"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Length (inches)</Label>
                  <Input
                    type="number"
                    value={newMaterial.length}
                    onChange={(e) => setNewMaterial(prev => ({ ...prev, length: Number(e.target.value) }))}
                    placeholder="Enter Length"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Outer Diameter</Label>
                  <Select
                    value={newMaterial.outerDiameter?.toString()}
                    onValueChange={(value) => setNewMaterial(prev => ({ ...prev, outerDiameter: Number(value) }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select outer diameter" />
                    </SelectTrigger>
                    <SelectContent>
                      {pipeSize.map((pipe, index) => (
                        <SelectItem key={index} value={pipe.size.toString()}>
                          {pipe.size} inches
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Wall Thickness</Label>
                  <Select
                    value={newMaterial.wallThickness?.toString()}
                    onValueChange={(value) => setNewMaterial(prev => ({ ...prev, wallThickness: Number(value) }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select wall thickness" />
                    </SelectTrigger>
                    <SelectContent>
                      {pipeSize.map((pipe, index) => (
                        <SelectItem key={index} value={pipe.wallThickness.toString()}>
                          {pipe.wallThickness} inches
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {newMaterial.category === 'Forge' && (
              <>
                <div className="space-y-2">
                  <Label>Forge NRI</Label>
                  <Input
                    value={newMaterial.forgeNRI}
                    onChange={(e) => setNewMaterial(prev => ({ ...prev, forgeNRI: e.target.value }))}
                    placeholder="Enter Forge NRI"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Forge Order Number</Label>
                  <Input
                    value={newMaterial.forgeOrderNumber}
                    onChange={(e) => setNewMaterial(prev => ({ ...prev, forgeOrderNumber: e.target.value }))}
                    placeholder="Enter Forge Order Number"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Forge Serial Number</Label>
                  <Input
                    value={newMaterial.forgeSerialNumber}
                    onChange={(e) => setNewMaterial(prev => ({ ...prev, forgeSerialNumber: e.target.value }))}
                    placeholder="Enter Forge Serial Number"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Forge Heat Number</Label>
                  <Input
                    value={newMaterial.forgeHeatNumber}
                    onChange={(e) => setNewMaterial(prev => ({ ...prev, forgeHeatNumber: e.target.value }))}
                    placeholder="Enter Forge Heat Number"
                  />
                </div>
              </>
            )}

            {newMaterial.category === 'Wire Lot' && (
              <div className="space-y-2">
                <Label>Lot Number</Label>
                <Input
                  value={newMaterial.lotNumber}
                  onChange={(e) => setNewMaterial(prev => ({ ...prev, lotNumber: e.target.value }))}
                  placeholder="Enter Lot Number"
                />
              </div>
            )}

            {newMaterial.category === 'Gas' && (
              <div className="space-y-2">
                <Label>Gas Serial Number</Label>
                <Input
                  value={newMaterial.gasSerialNumber}
                  onChange={(e) => setNewMaterial(prev => ({ ...prev, gasSerialNumber: e.target.value }))}
                  placeholder="Enter Gas Serial Number"
                />
              </div>
            )}

            {/* Document Upload Section - Available for Pipe and Forge */}
            {(newMaterial.category === 'Pipe' || newMaterial.category === 'Forge') && (
              <div className="col-span-2 space-y-4">
                <div className="space-y-2">
                  <Label>Upload Documents</Label>
                  <Input
                    type="file"
                    multiple
                    onChange={handleMaterialFileSelect}
                    className="cursor-pointer"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Document Tags</Label>
                  <div className="flex flex-wrap gap-2">
                    {documentTags.map(tag => (
                      <label key={tag.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={selectedTags.includes(tag.name)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTags([...selectedTags, tag.name]);
                            } else {
                              setSelectedTags(selectedTags.filter(t => t !== tag.name));
                            }
                          }}
                          className="rounded border-gray-300"
                        />
                        <span>{tag.name}</span>
                      </label>
                    ))}
                    <Button
                      onClick={() => setIsAddingTag(true)}
                      className="text-sm text-[#e04530] hover:text-[#e04530]/80"
                      variant="outline"
                    >
                      + Add New Tag
                    </Button>
                  </div>
                </div>

                {/* Add New Tag Dialog */}
                <Dialog open={isAddingTag} onOpenChange={setIsAddingTag}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add New Tag</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Tag Name</Label>
                        <Input
                          value={newTag}
                          onChange={(e) => setNewTag(e.target.value)}
                          placeholder="Enter tag name"
                        />
                      </div>
                      <div className="flex justify-end">
                        <Button
                          onClick={handleAddTag}
                          className="bg-[#e04530] text-white px-4 py-2 rounded-lg hover:bg-[#e04530]/90 transition-colors"
                          disabled={!newTag.trim()}
                        >
                          Add Tag
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            )}

            <div className="col-span-2 flex justify-end">
              <Button
                onClick={handleAddMaterial}
                className="bg-[#e04530] text-white px-4 py-2 rounded-lg hover:bg-[#e04530]/90 transition-colors"
                disabled={!newMaterial.category}
               
              >
                Add Material
                </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 